import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  DHome,
  DShop,
  DSparkle,
  DHeart,
  DCart,
  DCompare,
  DCard,
  DTruck,
  DReturn,
  DUser,
  DInfo,
  DMail,
  DTag,
  DBuilding,
  DBox,
  DShield,
  DDoc,
  DClose,
  DSearch,
  DPhone,
  DChevron,
} from "./sub-components/drawer-icons";

const PU = process.env.PUBLIC_URL;

/*
 * App-style navigation drawer.
 *
 * The open/close contract is unchanged: HeaderOne (and IconGroup on the
 * other layouts) still opens it by adding `.active` to
 * `#offcanvas-mobile-menu`, so every existing trigger keeps working. Only
 * the markup and styling are new — routes are the same ones the desktop
 * menu links to.
 */
const MobileMenu = ({ user }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const { cartItems } = useSelector((state) => state.cart);
  const { wishlistItems } = useSelector((state) => state.wishlist);
  const { compareItems } = useSelector((state) => state.compare);

  /* The signed-in user is passed down rather than queried here: the header
     that renders this drawer already holds the profile query, and a second
     subscription would refetch it every time the drawer remounts. */
  const isLoggedIn = !!user;
  const cartCount = cartItems?.length || 0;
  const wishlistCount = wishlistItems?.length || 0;
  const compareCount = compareItems?.length || 0;

  const close = useCallback(() => {
    const el = document.querySelector("#offcanvas-mobile-menu");
    if (el) el.classList.remove("active");
    setOpen(false);
  }, []);

  /* The triggers toggle the class directly, so mirror that class back into
     React state to drive the scrim and body scroll lock. */
  useEffect(() => {
    const el = document.querySelector("#offcanvas-mobile-menu");
    if (!el) return undefined;
    const sync = () => setOpen(el.classList.contains("active"));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  /* Close on navigation. */
  useEffect(() => {
    close();
  }, [pathname, close]);

  /* Escape closes; the page behind must not scroll while it is open. */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, close]);

  const submitSearch = (e) => {
    e.preventDefault();
    // Same destination the desktop search uses.
    navigate(PU + "/shop-grid-standard");
    close();
  };

  const accountPath = isLoggedIn ? "/profile" : "/login-register";
  const initial = (user?.firstName || user?.name || user?.email || "?")
    .trim()
    .charAt(0)
    .toUpperCase();

  const rowBody = (Icon, label, count) => (
    <>
      <span className="c4drawer__rowicon">
        <Icon />
      </span>
      <span className="c4drawer__rowlabel">{label}</span>
      {count > 0 && <span className="c4drawer__count">{count}</span>}
      <span className="c4drawer__chev">
        <DChevron />
      </span>
    </>
  );

  /* `plain` renders a Link with no active state — used where two entries
     share a destination (Shop / New Arrivals) so only one lights up. */
  const row = (to, Icon, label, count, plain) => (
    <li key={to + label}>
      {plain ? (
        <Link to={PU + to} className="c4drawer__row">
          {rowBody(Icon, label, count)}
        </Link>
      ) : (
        <NavLink
          to={PU + to}
          end={to === "/"}
          className={({ isActive }) =>
            "c4drawer__row" + (isActive ? " is-active" : "")
          }
        >
          {rowBody(Icon, label, count)}
        </NavLink>
      )}
    </li>
  );

  return (
    <div
      className={"c4drawer" + (open ? " active" : "")}
      id="offcanvas-mobile-menu"
    >
      <div className="c4drawer__scrim" onClick={close} aria-hidden="true" />

      <aside
        className="c4drawer__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
      >
        {/* ---- Dark head: brand, close, account ---- */}
        <div className="c4drawer__head">
          <div className="c4drawer__brand">
            <Link to={PU + "/"} onClick={close} className="c4drawer__logo">
              <img src={PU + "/assets/img/logo/navlogo.png"} alt="CEY4HUB" />
            </Link>
            <button
              type="button"
              className="c4drawer__close"
              onClick={close}
              aria-label="Close menu"
              tabIndex={open ? 0 : -1}
            >
              <DClose />
            </button>
          </div>

          <Link
            to={PU + accountPath}
            className="c4drawer__account"
            onClick={close}
            tabIndex={open ? 0 : -1}
          >
            <span className="c4drawer__avatar" aria-hidden="true">
              {isLoggedIn ? initial : <DUser />}
            </span>
            <span className="c4drawer__acctext">
              {isLoggedIn ? (
                <>
                  <strong>
                    {[user.firstName, user.lastName].filter(Boolean).join(" ") ||
                      user.name ||
                      "My account"}
                  </strong>
                  <small>{user.email}</small>
                </>
              ) : (
                <>
                  <strong>Sign in / Register</strong>
                  <small>Orders, wishlist &amp; faster checkout</small>
                </>
              )}
            </span>
            <span className="c4drawer__chev">
              <DChevron />
            </span>
          </Link>
        </div>

        {/* ---- Scrolling body ---- */}
        <div className="c4drawer__body">
          <form className="c4drawer__search" onSubmit={submitSearch} role="search">
            <span className="c4drawer__searchicon">
              <DSearch />
            </span>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products…"
              aria-label="Search products"
              tabIndex={open ? 0 : -1}
            />
          </form>

          <p className="c4drawer__label">Browse</p>
          <ul className="c4drawer__list">
            {row("/", DHome, t("home"))}
            {row("/shop-grid-standard", DShop, t("shop"))}
            {row("/shop-grid-standard", DSparkle, "New Arrivals", 0, true)}
            {row("/wishlist", DHeart, t("wishlist"), wishlistCount)}
            {row("/cart", DCart, t("cart"), cartCount)}
            {row("/compare", DCompare, t("compare"), compareCount)}
            {row("/checkout", DCard, t("checkout"))}
          </ul>

          <p className="c4drawer__label">Orders</p>
          <ul className="c4drawer__list">
            {row("/order-status", DTruck, "Track Your Order")}
            {row("/return-status", DReturn, "Returns")}
            {row(accountPath, DUser, t("my_account"))}
          </ul>

          <p className="c4drawer__label">Company</p>
          <ul className="c4drawer__list">
            {row("/about", DInfo, t("about_us"))}
            {row("/contact", DMail, t("contact_us"))}
            {/* Collaboration entry hidden for now. */}
            {row("/reselling", DTag, "Reselling")}
            {row("/franchise", DBuilding, "Franchise")}
            {row("/wholesale", DBox, "Wholesale")}
          </ul>

          <p className="c4drawer__label">Policies</p>
          <ul className="c4drawer__list">
            {row("/refund", DReturn, "Refund Policy")}
            {row("/privacy-policy", DShield, "Privacy Policy")}
            {row("/Terms-and-Conditions", DDoc, "Terms & Conditions")}
          </ul>

          {/* ---- Contact + social ---- */}
          <div className="c4drawer__foot">
            <a className="c4drawer__contact" href="tel:+442033711782">
              <DPhone />
              <span>+44 (0) 20 3371 1782</span>
            </a>
            <a className="c4drawer__contact" href="mailto:info@cey4hub.co.uk">
              <DMail />
              <span>info@cey4hub.co.uk</span>
            </a>

            <div className="c4drawer__social">
              <a href="//facebook.com" title="Facebook" aria-label="Facebook">
                <i className="fa fa-facebook" />
              </a>
              <a href="//instagram.com" title="Instagram" aria-label="Instagram">
                <i className="fa fa-instagram" />
              </a>
              <a href="//twitter.com" title="Twitter" aria-label="Twitter">
                <i className="fa fa-twitter" />
              </a>
              <a href="//pinterest.com" title="Pinterest" aria-label="Pinterest">
                <i className="fa fa-pinterest" />
              </a>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default MobileMenu;
